import React, { useCallback, useState } from 'react';
import { getSharedData, getProjectState } from '@starflow/app/store';
import { convert2CompressedUrl } from '@shakkerai/util-image';
import { updateElement } from '../../helpers/canvas';

const thumbSize = 128;

const GhostElement = ({ data }) => {
  const { url, top, left, width, height } = data;

  const ratio = width / height;
  const thumbWidth = width > height ? thumbSize : thumbSize * ratio;
  const thumbHeight = width > height ? thumbSize / ratio : thumbSize;

  return (
    <img
      crossOrigin="anonymous"
      src={convert2CompressedUrl(url, 128)}
      style={{ top, left, width: thumbWidth, height: thumbHeight }}
      className="absolute z-10 -translate-x-1/2 -translate-y-1/2 pointer-events-none bg-black/10 opacity-80"
    />
  );
};

export const DragReceiver = React.memo((props) => {
  const { name, onDrop, accept = 'image', disabled, className, children } = props;
  const [ghostElement, setGhostElement] = useState(null);
  const [isDraggingOver, setDraggingOver] = useState(false);

  const handleMouseEnter = useCallback(
    (event) => {
      if (disabled) return;

      // 处理画布内的图片拖拽到参考图
      const draggingElement = getSharedData('dragging-element');

      if (draggingElement && draggingElement.type === accept) {
        draggingElement.top = event.nativeEvent.offsetY;
        draggingElement.left = event.nativeEvent.offsetX;

        setGhostElement(draggingElement);
        setDraggingOver(true);
      }
    },
    [accept, disabled],
  );

  const handleMouseMove = useCallback(
    (event) => {
      if (ghostElement) {
        setGhostElement((data) =>
          Object.assign({}, data, {
            top: event.nativeEvent.offsetY,
            left: event.nativeEvent.offsetX,
          }),
        );
      }
    },
    [ghostElement],
  );

  const handleMouseLeave = useCallback(() => {
    setGhostElement(null);
    setDraggingOver(false);
  }, []);

  const handleMouseUp = useCallback(() => {
    if (ghostElement) {
      // 图片插入
      // ghostElement的宽高是不准的
      const elements = getProjectState('elements');
      const aimElement = elements.find((item) => item.id === ghostElement.id);
      onDrop(
        {
          id: ghostElement.id,
          url: ghostElement.url,
          width: aimElement.width,
          height: aimElement.height,
          innerTaskId: aimElement.innerTaskId,
        },
        name,
      );

      // 还原图片位置
      setTimeout(() => {
        updateElement(ghostElement.id, { x: ghostElement.originX, y: ghostElement.originY });
      }, 50);
    }

    setGhostElement(null);
    setDraggingOver(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, onDrop, ghostElement]);

  return (
    <div
      onMouseUp={handleMouseUp}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`relative ${className}`}
    >
      {children(isDraggingOver)}
      {!!ghostElement && <GhostElement data={ghostElement} />}
    </div>
  );
});
